/**
 * What the browser keeps of a session.
 *
 * Only the short-lived access token lives in `localStorage`. The long-lived refresh token is an
 * HttpOnly cookie the server sets and the browser attaches on its own, so this module never sees
 * it — which is the point: scripts cannot read it, and Safari's seven-day purge of script-writable
 * storage cannot take it. What scripts *can* see is a marker cookie (`vgp_session`) the server
 * sets alongside it, which says "there is a session to resume" without saying anything else.
 *
 * `vgp_token` keeps its name: tests and any tab still running an older bundle read it.
 */

const ACCESS_KEY = 'vgp_token';
/**
 * Where the refresh token used to be stored. Read once, so a browser that signed in before it
 * became a cookie is carried over instead of signed out, and then deleted.
 */
const LEGACY_REFRESH_KEY = 'vgp_refresh_token';
const LEGACY_USER_KEY = 'vgp_user'; // written by the old SPA; we clear it but never read it
const SESSION_HINT_COOKIE = 'vgp_session';

/** Every storage access is guarded: private mode and blocked site data both throw. */
function safeGet(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSet(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* ignore — the session simply will not survive a reload */
  }
}

function safeRemove(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}

export function getAccessToken(): string | null {
  return safeGet(ACCESS_KEY);
}

export function setTokens(access: string): void {
  safeSet(ACCESS_KEY, access);
}

/** A refresh token stored by a version of the app that predates the cookie, if there is one. */
export function getLegacyRefreshToken(): string | null {
  return safeGet(LEGACY_REFRESH_KEY);
}

export function clearLegacyRefreshToken(): void {
  safeRemove(LEGACY_REFRESH_KEY);
}

/**
 * Does the server say this browser has a session to resume?
 *
 * It is a hint, not proof: the cookie can be stale. It exists so a returning visitor whose
 * access token has gone (storage cleared, or purged by Safari) still gets a refresh attempt,
 * while an anonymous one does not get a request that is certain to fail.
 */
export function hasSessionHint(): boolean {
  try {
    return document.cookie.split(';').some((part) => part.trim() === `${SESSION_HINT_COOKIE}=1`);
  } catch {
    return false;
  }
}

function clearSessionHint(): void {
  try {
    document.cookie = `${SESSION_HINT_COOKIE}=; Max-Age=0; Path=/`;
  } catch {
    /* ignore */
  }
}

/** Forget everything about the session on this device. */
export function clearTokens(): void {
  safeRemove(ACCESS_KEY);
  safeRemove(LEGACY_REFRESH_KEY);
  safeRemove(LEGACY_USER_KEY);
  clearSessionHint();
}

export function hasSession(): boolean {
  return Boolean(getAccessToken()) || hasSessionHint();
}

/* ───── Cross-tab session sync ───── */

const CHANNEL_NAME = 'vgp_session';

export type SessionEvent = { type: 'logout' } | { type: 'login' };

let channel: BroadcastChannel | null = null;

function getChannel(): BroadcastChannel | null {
  if (typeof BroadcastChannel === 'undefined') return null;
  if (!channel) channel = new BroadcastChannel(CHANNEL_NAME);
  return channel;
}

/** Tell the other tabs what just happened, so logging out here logs out everywhere. */
export function broadcastSession(event: SessionEvent): void {
  getChannel()?.postMessage(event);
}

export function onSessionEvent(handler: (event: SessionEvent) => void): () => void {
  const ch = getChannel();
  if (!ch) return () => {};
  const listener = (event: MessageEvent<SessionEvent>) => handler(event.data);
  ch.addEventListener('message', listener);
  return () => ch.removeEventListener('message', listener);
}
